import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Upload, DatePicker, message, Space, Progress, Modal, Radio } from 'antd';
import { UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { useMutation, useLazyQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ClientSelectWrapper from './ClientSelectWrapper';
import HubSelectWrapper from './HubSelectWrapper';
import { fileUpload } from '../../../../../utils/storageUtils';
import {
    IMPORT_CLIENT_HUB_ZONE_CONFIG,
    ImportClientHubZoneConfigVariables,
    ImportClientHubZoneConfigResponse
} from '../graphql/mutations/clientHubZoneMutations';
import {
    GET_CLIENT_HUB_ZONE_IMPORT_TASK,
    GetClientHubZoneImportTaskResponse
} from '../graphql/queries/getClientHubZoneImportTask';

dayjs.extend(utc);

const { Dragger } = Upload;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const ImportTab: React.FC = () => {
    const [form] = Form.useForm();
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);
    const [taskNo, setTaskNo] = useState<string | null>(null);
    const [progressVisible, setProgressVisible] = useState(false);
    const [taskStatus, setTaskStatus] = useState<any>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [importType, setImportType] = useState<'client' | 'fallback'>('client');

    const [importClientHubZoneConfig] = useMutation<
        ImportClientHubZoneConfigResponse,
        ImportClientHubZoneConfigVariables
    >(IMPORT_CLIENT_HUB_ZONE_CONFIG);

    const [getTask, { data: taskData }] = useLazyQuery<GetClientHubZoneImportTaskResponse>(
        GET_CLIENT_HUB_ZONE_IMPORT_TASK
    );

    const handleUpload = async (file: File) => {
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            message.error('请上传Excel文件（.xlsx, .xls）！');
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            message.error(`文件大小不能超过20MB，当前文件大小：${(file.size / 1024 / 1024).toFixed(2)}MB`);
            return false;
        }

        setFileList([{
            uid: Date.now().toString(),
            name: file.name,
            status: 'done',
            originFileObj: file
        }]);

        return false;
    };

    useEffect(() => {
        if (taskNo && progressVisible) {
            pollingIntervalRef.current = setInterval(() => {
                getTask({ variables: { taskNo } });
            }, 2000);

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
            };
        }
    }, [taskNo, progressVisible, getTask]);

    useEffect(() => {
        if (taskData?.getClientHubZoneImportTask) {
            const task = taskData.getClientHubZoneImportTask;
            setTaskStatus(task);

            if (task.status === 2 || task.status === 3) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }

                if (task.status === 2) {
                    message.success(`导入完成！共导入 ${task.importedCount} 条记录${task.failedCount > 0 ? `，失败 ${task.failedCount} 条` : ''}`);
                    form.resetFields();
                    setFileList([]);
                    setImportType('client');
                    setTimeout(() => {
                        setProgressVisible(false);
                        setTaskNo(null);
                        setTaskStatus(null);
                    }, 3000);
                } else {
                    message.error(`导入失败：${task.errorMessage || '未知错误'}`);
                }
            }
        }
    }, [taskData, form]);

    const handleSubmit = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();

            if (fileList.length === 0) {
                message.error('请先上传文件！');
                return;
            }

            setUploading(true);

            const fileObj = fileList[0].originFileObj;
            const { objectKey } = await fileUpload(fileObj, 'client-hub-zone');

            // 用户选择的时间已经是UTC+0时间，直接转换为时间戳
            const activeTime = values.activeTime
                ? dayjs.utc(values.activeTime).unix()
                : dayjs.utc().unix();

            // 根据导入类型确定 channelId
            const channelId = values.importType === 'fallback' ? 0 : values.channelId;

            const result = await importClientHubZoneConfig({
                variables: {
                    input: {
                        objectKey,
                        filename: fileObj.name,
                        channelId: channelId,
                        hubId: values.hubId,
                        activeTime
                    }
                }
            });

            if (result.data?.importClientHubZoneConfig) {
                const response = result.data.importClientHubZoneConfig;
                setTaskNo(response.taskNo);
                setProgressVisible(true);
                message.success('任务已提交，正在处理中...');
            }
        } catch (error: any) {
            message.error(error.message || '提交失败');
        } finally {
            setUploading(false);
        }
    };

    const getProgressPercent = () => {
        if (!taskStatus || taskStatus.totalCount === 0) return 0;
        return Math.round((taskStatus.importedCount / taskStatus.totalCount) * 100);
    };

    const getStatusText = () => {
        if (!taskStatus) return '准备中...';
        switch (taskStatus.status) {
            case 0: return '等待处理';
            case 1: return '处理中';
            case 2: return '完成';
            case 3: return '失败';
            default: return '未知状态';
        }
    };

    return (
        <div className="p-6">
            <Card
                title="导入 Client Hub Zone 配置"
                className="max-w-4xl mx-auto"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        activeTime: dayjs.utc(),
                        importType: 'client'
                    }}
                >
                    <Form.Item
                        label="导入类型"
                        name="importType"
                        rules={[{ required: true, message: '请选择导入类型' }]}
                    >
                        <Radio.Group
                            value={form.getFieldValue('importType') || 'client'}
                            onChange={(e) => {
                                const type = e.target.value;
                                setImportType(type);
                                if (type === 'fallback') {
                                    // 选择兜底客户时，清空 channelId，不在选择器中显示任何值
                                    form.setFieldsValue({ channelId: undefined });
                                } else {
                                    // 选择客户时，清空 channelId，让用户重新选择
                                    form.setFieldsValue({ channelId: undefined });
                                }
                            }}
                        >
                            <Radio value="client">客户</Radio>
                            <Radio value="fallback">兜底客户</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label="客户"
                        name="channelId"
                        rules={[
                            {
                                validator: (_, value) => {
                                    const currentType = form.getFieldValue('importType');
                                    if (currentType === 'fallback') {
                                        // 兜底客户时，channelId 自动为 0，不需要验证
                                        return Promise.resolve();
                                    }
                                    // 客户类型时，必须选择客户
                                    if (!value) {
                                        return Promise.reject(new Error('请选择客户'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <ClientSelectWrapper 
                            placeholder={importType === 'fallback' ? '兜底客户（无需选择）' : '请选择客户'}
                            disabled={importType === 'fallback'}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Hub"
                        name="hubId"
                        rules={[{ required: true, message: '请选择Hub' }]}
                    >
                        <HubSelectWrapper placeholder="请选择Hub" />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                生效时间
                                <span className="ml-2 text-orange-600 font-semibold">（UTC+0）</span>
                            </span>
                        }
                        name="activeTime"
                        rules={[
                            { required: true, message: '请选择生效时间' },
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.resolve();
                                    }
                                    // 生效时间不能早于当前UTC时间
                                    const nowUtc = dayjs.utc();
                                    if (value.isBefore(nowUtc, 'second')) {
                                        return Promise.reject(new Error('生效时间不能早于当前时间（UTC+0）'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                        tooltip="配置从这个时间开始生效（UTC+0时间），不能早于当前时间"
                        extra={
                            <div className="mt-1">
                                <span className="text-orange-600 text-sm font-medium">
                                    ⚠️ 注意：此处输入的是 UTC+0 时间，不是本地时间
                                </span>
                                <div className="text-gray-500 text-xs mt-1">
                                    当前 UTC+0 时间：{dayjs.utc().format('YYYY-MM-DD HH:mm:ss')} UTC+0
                                    {dayjs().utcOffset() !== 0 && (
                                        <span className="ml-2">
                                            （您的本地时间：{dayjs().format('YYYY-MM-DD HH:mm:ss')} {dayjs().format('Z')}）
                                        </span>
                                    )}
                                </div>
                            </div>
                        }
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            className="w-full"
                            placeholder="请选择生效时间（UTC+0）"
                            value={form.getFieldValue('activeTime') ? dayjs.utc(form.getFieldValue('activeTime')) : undefined}
                            onChange={(date) => {
                                // 将选择的日期转换为UTC时间
                                form.setFieldsValue({ activeTime: date ? dayjs.utc(date.format('YYYY-MM-DD HH:mm:ss')) : null });
                            }}
                            disabledDate={(current) => {
                                if (!current) return false;
                                // 禁用今天之前的日期（UTC时间）
                                const nowUtc = dayjs.utc();
                                return current.isBefore(nowUtc.startOf('day'), 'day');
                            }}
                            disabledTime={(current) => {
                                if (!current) {
                                    return {};
                                }
                                // 如果是今天，禁用当前UTC时间之前的时间
                                const nowUtc = dayjs.utc();
                                if (current.isSame(nowUtc, 'day')) {
                                    return {
                                        disabledHours: () => {
                                            const hours = [];
                                            for (let i = 0; i < nowUtc.hour(); i++) {
                                                hours.push(i);
                                            }
                                            return hours;
                                        },
                                        disabledMinutes: (selectedHour: number) => {
                                            if (selectedHour === nowUtc.hour()) {
                                                const minutes = [];
                                                for (let i = 0; i < nowUtc.minute(); i++) {
                                                    minutes.push(i);
                                                }
                                                return minutes;
                                            }
                                            return [];
                                        },
                                        disabledSeconds: (selectedHour: number, selectedMinute: number) => {
                                            if (selectedHour === nowUtc.hour() && selectedMinute === nowUtc.minute()) {
                                                const seconds = [];
                                                for (let i = 0; i < nowUtc.second(); i++) {
                                                    seconds.push(i);
                                                }
                                                return seconds;
                                            }
                                            return [];
                                        }
                                    };
                                }
                                return {};
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="上传Excel文件"
                        required
                        tooltip="文件最大支持20MB，格式要求：包含zipcode和zone两列"
                        extra={
                            <div className="mt-2 space-y-2">
                                <div className="text-sm text-gray-600">
                                    <div className="font-semibold mb-1">📋 Excel文件格式要求：</div>
                                    <div className="pl-4 space-y-1 text-xs">
                                        <div>1. <strong>表头格式</strong>：必须包含以下两列（不区分大小写，支持前10行自动检测）</div>
                                        <div className="pl-4 space-y-1">
                                            <div>• <span className="font-mono bg-gray-100 px-1 rounded">zipcode</span> 列（支持：<span className="font-mono">zipcode</span>、<span className="font-mono">zip code</span>、<span className="font-mono">zip</span>）</div>
                                            <div>• <span className="font-mono bg-gray-100 px-1 rounded">zone</span> 列（必须包含：<span className="font-mono">zone</span>）</div>
                                        </div>
                                        <div>2. <strong>数据格式要求</strong>：</div>
                                        <div className="pl-4 space-y-1">
                                            <div>• <span className="font-mono">zipcode</span>：必须是有效的5位数字</div>
                                            <div>• <span className="font-mono">zone</span>：文本，最多10个字符</div>
                                        </div>
                                        <div>3. <strong>示例格式</strong>：</div>
                                        <div className="pl-4">
                                            <table className="border-collapse border border-gray-300 text-xs mt-1">
                                                <thead>
                                                    <tr className="bg-gray-100">
                                                        <th className="border border-gray-300 px-2 py-1">zipcode</th>
                                                        <th className="border border-gray-300 px-2 py-1">zone</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="border border-gray-300 px-2 py-1">12345</td>
                                                        <td className="border border-gray-300 px-2 py-1">A</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="border border-gray-300 px-2 py-1">67890</td>
                                                        <td className="border border-gray-300 px-2 py-1">B</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="text-orange-600 mt-2">
                                            ⚠️ 注意：zipcode或zone为空的行的数据将被跳过，统计为失败记录
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <Dragger
                            fileList={fileList}
                            beforeUpload={handleUpload}
                            onRemove={() => setFileList([])}
                            maxCount={1}
                            accept=".xlsx,.xls"
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                            <p className="ant-upload-hint">
                                支持 .xlsx 和 .xls 格式，文件大小不超过20MB
                            </p>
                        </Dragger>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            size="large"
                            loading={uploading}
                            onClick={handleSubmit}
                            block
                        >
                            提交导入
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <Modal
                title="导入进度"
                open={progressVisible}
                footer={null}
                closable={taskStatus?.status === 2 || taskStatus?.status === 3}
                onCancel={() => {
                    setProgressVisible(false);
                    setTaskNo(null);
                    setTaskStatus(null);
                }}
                width={600}
            >
                <div className="space-y-4">
                    <div>
                        <div className="mb-2 flex justify-between">
                            <span className="text-gray-600">任务编号:</span>
                            <span className="font-mono text-blue-600">{taskNo}</span>
                        </div>
                        <div className="mb-2 flex justify-between">
                            <span className="text-gray-600">状态:</span>
                            <span className="font-semibold">{getStatusText()}</span>
                        </div>
                    </div>

                    <Progress
                        percent={getProgressPercent()}
                        status={taskStatus?.status === 3 ? 'exception' : taskStatus?.status === 2 ? 'success' : 'active'}
                        format={() => taskStatus ? `${taskStatus.importedCount}/${taskStatus.totalCount}` : '0/0'}
                    />

                    {taskStatus && (
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="bg-blue-50 p-3 rounded">
                                <div className="text-2xl font-bold text-blue-600">{taskStatus.totalCount}</div>
                                <div className="text-sm text-gray-600">总记录数</div>
                            </div>
                            <div className="bg-green-50 p-3 rounded">
                                <div className="text-2xl font-bold text-green-600">{taskStatus.importedCount}</div>
                                <div className="text-sm text-gray-600">已导入</div>
                            </div>
                            <div className="bg-red-50 p-3 rounded">
                                <div className="text-2xl font-bold text-red-600">{taskStatus.failedCount}</div>
                                <div className="text-sm text-gray-600">失败</div>
                            </div>
                        </div>
                    )}

                    {taskStatus?.errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded p-3">
                            <div className="text-red-600 font-semibold mb-1">错误信息:</div>
                            <div className="text-red-500 text-sm">{taskStatus.errorMessage}</div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ImportTab;

